import type {
  UserDashboardData,
  TopicMastery,
  WeakTopic,
} from '../../../types/admin';
import type {
  DashboardVM,
  DomainVM,
  DrillVM,
  PathState,
  Difficulty,
} from './types';
import { DOMAIN_PALETTE, getIqTier } from './palette';

/**
 * Derive a stable `key` from a topic name (slug-safe, stable across renders).
 */
function topicKey(topic: string): string {
  return topic
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function pickDifficulty(accuracy: number): Difficulty {
  if (accuracy < 50) return 'hard';
  if (accuracy < 70) return 'med';
  return 'easy';
}

/**
 * Build the Variant B view-model from the existing dashboard payload.
 *
 * Strategy:
 *  - Each `topic_mastery` entry becomes one domain card, capped at the
 *    palette length (5).
 *  - Path order: completed (mastery ≥ 80) first, then the smart-study
 *    suggestion as the current step, then the rest as `todo`.
 *  - Drills come from `weak_topics`; the worst weak topic also fills the
 *    "Recommended" hero.
 *  - Global Skill IQ = readiness score; delta = improvement.delta.
 */
export function buildDashboardVM(
  data: UserDashboardData,
  certPassingScore: number,
  avgSecondsPerQuestion: number | null,
): DashboardVM {
  const passMark = certPassingScore || 70;
  const masteries: TopicMastery[] = data.topic_mastery ?? [];
  const weakTopics: WeakTopic[] = data.weak_topics ?? [];

  // ── Per-topic time map (for est minutes) ────────────────────────────
  const topicTime = new Map<string, number>();
  for (const t of data.time_analytics?.by_topic ?? []) {
    topicTime.set(t.topic, t.avg_seconds);
  }
  const defaultAvgSec = avgSecondsPerQuestion ?? 60;

  // ── Domains ─────────────────────────────────────────────────────────
  const totalQuestions = masteries.reduce((acc, t) => acc + t.total_questions, 0) || 1;
  const sorted = [...masteries].sort((a, b) => b.total_questions - a.total_questions);
  const domains: DomainVM[] = sorted.slice(0, DOMAIN_PALETTE.length).map((t, idx) => {
    const pal = DOMAIN_PALETTE[idx];
    const lessons = t.total_questions;
    const done = t.answered;
    const remaining = Math.max(lessons - done, 0);
    const secPerQ = topicTime.get(t.topic) ?? defaultAvgSec;
    const mins = Math.max(1, Math.round((remaining * secPerQ) / 60));
    const weight = Math.round((t.total_questions / totalQuestions) * 100);
    const progress = lessons > 0 ? done / lessons : 0;
    const mastery = Math.round(t.mastery_percent);
    // No historical delta in payload — leave 0 (UI will hide the up arrow).
    const iqDelta = 0;
    const level = mastery >= 80 ? 'advanced' : mastery >= passMark ? 'intermediate' : 'beginner';
    return {
      code: String(idx + 1).padStart(2, '0'),
      key: topicKey(t.topic),
      name: t.topic,
      icon: pal.icon,
      gradient: pal.gradient,
      dot: pal.dot,
      weight,
      progress,
      mastery,
      iqDelta,
      lessons,
      done,
      mins,
      levelKey: `dashboard.variantB.level.${level}`,
    };
  });

  // ── Path sequence (done → cur → todo) ───────────────────────────────
  const smartTopic = data.smart_study?.suggested_topic ?? null;
  const smartKey = smartTopic ? topicKey(smartTopic) : null;
  const pathSequence: DomainVM[] = (() => {
    if (domains.length === 0) return [];
    const done: DomainVM[] = [];
    const cur: DomainVM[] = [];
    const todo: DomainVM[] = [];
    let curPicked = false;
    for (const d of domains) {
      if (d.mastery >= 80) {
        done.push({ ...d, state: 'done' as PathState });
      } else if (!curPicked && (smartKey === null || d.key === smartKey)) {
        cur.push({ ...d, state: 'cur' as PathState });
        curPicked = true;
      } else {
        todo.push({ ...d, state: 'todo' as PathState });
      }
    }
    // If nothing matched the smart suggestion, pick the lowest-mastery non-done as current.
    if (!curPicked && todo.length > 0) {
      todo.sort((a, b) => a.mastery - b.mastery);
      const first = todo.shift()!;
      cur.push({ ...first, state: 'cur' as PathState });
    }
    return [...done, ...cur, ...todo];
  })();

  // ── Drills (from weak topics) ───────────────────────────────────────
  const drills: DrillVM[] = weakTopics.slice(0, 6).map((w, idx) => {
    const matchDomain = domains.find((d) => d.name === w.topic);
    const dot = matchDomain?.dot ?? DOMAIN_PALETTE[idx % DOMAIN_PALETTE.length].dot;
    const secPerQ = topicTime.get(w.topic) ?? defaultAvgSec;
    const q = Math.min(15, Math.max(8, w.total_answered || 10));
    const mins = Math.max(8, Math.round((q * secPerQ) / 60));
    return {
      id: `drill-${topicKey(w.topic)}-${idx}`,
      name: w.topic,
      domName: w.topic,
      domKey: topicKey(w.topic),
      domDot: dot,
      diff: pickDifficulty(w.accuracy_percent),
      q,
      mins,
      why: '',
      href: `/study?topic=${encodeURIComponent(w.topic)}`,
    };
  });

  // ── Recommended (first weak topic, or null) ─────────────────────────
  const recommended: DrillVM | null = drills[0] ?? null;

  // ── Score trend (re-index with attempt #) ───────────────────────────
  const scoreTrend = (data.score_trend ?? []).map((p, i) => ({
    n: i + 1,
    score: Math.round(p.score_percent),
    date: p.date,
  }));

  // ── Global Skill IQ from readiness ──────────────────────────────────
  const readinessScore = data.readiness?.score ?? 0;
  const tier = getIqTier(readinessScore, passMark);
  const globalIQ = data.readiness
    ? {
        score: Math.round(readinessScore),
        target: passMark,
        delta: Math.round(data.improvement?.delta ?? 0),
        percentile: null,
        peers: null,
        attempts: data.overview?.exam_count ?? 0,
        tier,
      }
    : null;

  // ── Next milestone (drives the right-side mini card) ────────────────
  const nextMilestone =
    globalIQ === null
      ? null
      : (() => {
          const ladder = [passMark, 80, 90, 100];
          const targetScore = ladder.find((v) => v > globalIQ.score) ?? 100;
          const pointsToGo = Math.max(0, targetScore - globalIQ.score);
          const progressPct = Math.max(0, Math.min(100, Math.round((globalIQ.score / targetScore) * 100)));
          const sessionsEstimate = Math.max(1, Math.ceil(pointsToGo / 2));
          return { targetScore, pointsToGo, sessionsEstimate, progressPct };
        })();

  return {
    domains,
    pathSequence,
    drills,
    scoreTrend,
    globalIQ,
    recommended,
    nextMilestone,
    weightTotal: domains.reduce((acc, d) => acc + d.weight, 0),
  };
}
